import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SweetAlertService } from '@core/services/sweet-alert.service';
import { PublicBillSplitFacade } from '../../services/public-bill-split-facade';
import {
  CollaborativeBillSplitItemResponseDto,
  ParticipantBillSplitContextResponseDto,
  SaveParticipantProposalsRequestDto,
} from '@features/client/models/public-bill-split-collaboration-models';
import { HttpErrorLike } from '@core/models/http-error-like.model';
import {
  EqualShareScope,
  ParticipantItemSelectionDraft,
  ParticipantSuggestedGroupDraft,
} from '@features/client/models/participant-selection.models';
import { ParticipantItemCardComponent } from '../../components/participant-item-card/participant-item-card';
import { ParticipantItemGroupsModalComponent } from '../../components/participant-item-groups-modal/participant-item-groups-modal';
import { ParticipantSubmittedItemCardComponent } from '../../components/participant-submitted-item-card/participant-submitted-item-card';
import { Subscription } from 'rxjs';
import { CollaborativeSseEvent } from '@features/client/models/public-bill-split-sse-models';
import { httpErrorCode } from '@core/utils/http-error';

import { logger } from '@core/utils/logger';
@Component({
  selector: 'app-public-bill-split-participant-page',
  standalone: true,
  imports: [
    CommonModule,
    ParticipantItemCardComponent,
    ParticipantItemGroupsModalComponent,
    ParticipantSubmittedItemCardComponent,
  ],
  templateUrl: './public-bill-split-participant-page.html',
})
export class PublicBillSplitParticipantPageComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private facade = inject(PublicBillSplitFacade);
  private sweetAlert = inject(SweetAlertService);

  private realtimeSub?: Subscription;

  readonly splitId = this.route.snapshot.paramMap.get('splitId') ?? '';
  readonly participantToken = sessionStorage.getItem('dinno_join_participant_token') ?? '';
  readonly participantId = sessionStorage.getItem('dinno_join_participant_id') ?? '';
  readonly participantName =
    sessionStorage.getItem('dinno_join_participant_name') ?? 'Participante';

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly waiting = signal(false);

  readonly context = signal<ParticipantBillSplitContextResponseDto | null>(null);
  readonly drafts = signal<ParticipantItemSelectionDraft[]>([]);

  readonly unresolvedModalOpen = signal(false);
  readonly unresolvedModalItemId = signal<string | null>(null);

  readonly title = computed(() => this.context()?.title ?? 'Cuenta');
  readonly items = computed(() => this.context()?.items ?? []);

  readonly unresolvedItem = computed(() => {
    const itemId = this.unresolvedModalItemId();
    if (!itemId) return null;

    return this.items().find((i) => i.itemId === itemId) ?? null;
  });

  readonly alreadySubmitted = computed(() => {
    if (this.wasSubmittedLocally()) return true;

    const context = this.context();
    if (!context) return false;

    return context.participants.some(
      (participant) => participant.id === this.participantId && participant.status === 'SUBMITTED',
    );
  });

  readonly submittedItems = computed(() =>
    this.items().filter((item) => {
      const draft = this.getDraftForItem(item.itemId);
      return draft.selected && !!draft.mode;
    }),
  );

  readonly selectedConsumptionsCount = computed(() =>
    this.drafts().reduce((acc, draft) => {
      if (!draft.selected || !draft.mode) return acc;

      if (draft.mode === 'BY_QUANTITY') {
        return acc + (draft.quantity ?? 0);
      }

      if (draft.mode === 'EQUAL_SHARE') {
        if (draft.equalShareScope === 'FULL_ITEM') {
          const item = this.items().find((i) => i.itemId === draft.itemId);
          return acc + (item?.quantity ?? 0);
        }

        return acc + (draft.equalShareQuantity ?? 0);
      }

      if (draft.mode === 'UNRESOLVED') {
        const totalSuggested = (draft.suggestedGroups ?? []).reduce(
          (sum, group) => sum + group.size,
          0,
        );

        return acc + (totalSuggested > 0 ? totalSuggested : 1);
      }

      return acc;
    }, 0),
  );

  readonly hasSubmittableSelection = computed(() =>
    this.drafts().some((draft) => {
      if (!draft.selected || !draft.mode) return false;

      if (draft.mode === 'BY_QUANTITY') {
        return (draft.quantity ?? 0) > 0;
      }

      if (draft.mode === 'EQUAL_SHARE') {
        if (draft.equalShareScope === 'FULL_ITEM') return true;
        return (draft.equalShareQuantity ?? 0) > 0;
      }

      if (draft.mode === 'UNRESOLVED') {
        return true;
      }

      return false;
    }),
  );

  ngOnInit(): void {
    if (!this.splitId || !this.participantToken) {
      this.loading.set(false);
      this.sweetAlert.showError('No se pudo abrir la cuenta', 'Falta información para continuar.');
      return;
    }

    if (this.wasSubmittedLocally()) {
      this.waiting.set(true);
    }

    this.facade.getParticipantContext(this.splitId, this.participantToken).subscribe({
      next: (context) => {
        this.context.set(context);
        this.drafts.set(this.buildDrafts(context.items));
        this.loading.set(false);

        this.subscribeToRealtimeEvents();

        if (this.wasSubmittedLocally() || this.isCurrentParticipantSubmitted(context)) {
          this.waiting.set(true);
        }
      },
      error: (err) =>
        this.handleError(
          err,
          'No se pudo cargar la cuenta',
          'Intentá nuevamente en unos segundos.',
        ),
    });
  }

  ngOnDestroy(): void {
    this.realtimeSub?.unsubscribe();
  }

  toggleItem(itemId: string): void {
    this.drafts.update((drafts) =>
      drafts.map((draft) => {
        if (draft.itemId !== itemId) return draft;

        const nextSelected = !draft.selected;

        return {
          ...draft,
          selected: nextSelected,
          expanded: nextSelected,
          mode: nextSelected ? (draft.mode ?? 'EQUAL_SHARE') : null,
          equalShareScope: nextSelected ? (draft.equalShareScope ?? 'FULL_ITEM') : null,
          equalShareQuantity: nextSelected ? draft.equalShareQuantity : null,
          quantity: nextSelected ? draft.quantity : null,
        };
      }),
    );
  }

  updateMode(itemId: string, mode: 'EQUAL_SHARE' | 'BY_QUANTITY'): void {
    this.drafts.update((drafts) =>
      drafts.map((draft) => {
        if (draft.itemId !== itemId) return draft;

        const item = this.items().find((i) => i.itemId === itemId);
        const maxQty = item?.quantity ?? 1;

        if (mode === 'EQUAL_SHARE') {
          return {
            ...draft,
            selected: true,
            expanded: true,
            mode,
            equalShareScope: draft.equalShareScope ?? 'FULL_ITEM',
            equalShareQuantity:
              draft.equalShareScope === 'PARTIAL_QUANTITY' ? (draft.equalShareQuantity ?? 1) : null,
            quantity: null,
          };
        }

        return {
          ...draft,
          selected: true,
          expanded: true,
          mode,
          equalShareScope: null,
          equalShareQuantity: null,
          quantity: draft.quantity ?? Math.min(1, maxQty),
        };
      }),
    );
  }

  updateEqualShareScope(itemId: string, scope: EqualShareScope): void {
    this.drafts.update((drafts) =>
      drafts.map((draft) => {
        if (draft.itemId !== itemId) return draft;

        return {
          ...draft,
          selected: true,
          expanded: true,
          mode: 'EQUAL_SHARE',
          equalShareScope: scope,
          equalShareQuantity: scope === 'PARTIAL_QUANTITY' ? (draft.equalShareQuantity ?? 1) : null,
        };
      }),
    );
  }

  updateEqualShareQuantity(itemId: string, quantity: number): void {
    this.drafts.update((drafts) =>
      drafts.map((draft) =>
        draft.itemId === itemId
          ? {
              ...draft,
              selected: true,
              expanded: true,
              mode: 'EQUAL_SHARE',
              equalShareScope: 'PARTIAL_QUANTITY',
              equalShareQuantity: quantity,
            }
          : draft,
      ),
    );
  }

  updateQuantity(itemId: string, quantity: number): void {
    this.drafts.update((drafts) =>
      drafts.map((draft) =>
        draft.itemId === itemId
          ? {
              ...draft,
              selected: true,
              expanded: true,
              mode: 'BY_QUANTITY',
              quantity,
            }
          : draft,
      ),
    );
  }

  markUnresolved(itemId: string): void {
    this.openUnresolvedModal(itemId);
  }

  submitSelection(): void {
    if (!this.splitId || !this.participantId || !this.participantToken) return;

    if (this.wasSubmittedLocally()) {
      this.waiting.set(true);
      return;
    }

    const payload = this.buildSaveDraftPayload();

    this.saving.set(true);

    this.facade
      .saveParticipantSelectionDraft(
        this.splitId,
        this.participantId,
        this.participantToken,
        payload,
      )
      .subscribe({
        next: () => {
          this.facade
            .submitParticipantSelection(this.splitId, this.participantId, this.participantToken)
            .subscribe({
              next: () => {
                this.saving.set(false);
                this.waiting.set(true);
                sessionStorage.setItem(`dinno_participant_submitted_${this.splitId}`, 'true');
              },
              error: (err) =>
                this.handleError(err, 'No se pudo enviar la selección', 'Intentá nuevamente.'),
            });
        },
        error: (err) =>
          this.handleError(
            err,
            'No se pudo guardar la selección',
            'Intentá nuevamente en unos segundos.',
          ),
      });
  }

  getDraftForItem(itemId: string): ParticipantItemSelectionDraft {
    return (
      this.drafts().find((draft) => draft.itemId === itemId) ?? {
        itemId,
        selected: false,
        expanded: false,
        mode: null,
        equalShareScope: null,
        equalShareQuantity: null,
        quantity: null,
        suggestedGroups: [],
      }
    );
  }

  openUnresolvedModal(itemId: string): void {
    this.unresolvedModalItemId.set(itemId);
    this.unresolvedModalOpen.set(true);
  }

  closeUnresolvedModal(): void {
    this.unresolvedModalOpen.set(false);
    this.unresolvedModalItemId.set(null);
  }

  saveUnresolvedGroups(groups: ParticipantSuggestedGroupDraft[]): void {
    const itemId = this.unresolvedModalItemId();
    if (!itemId) return;

    this.drafts.update((drafts) =>
      drafts.map((draft) =>
        draft.itemId === itemId
          ? {
              ...draft,
              selected: true,
              expanded: true,
              mode: 'UNRESOLVED',
              equalShareScope: null,
              equalShareQuantity: null,
              quantity: null,
              suggestedGroups: groups,
            }
          : draft,
      ),
    );

    this.closeUnresolvedModal();
  }

  private buildDrafts(
    items: CollaborativeBillSplitItemResponseDto[],
  ): ParticipantItemSelectionDraft[] {
    return items.map((item) => ({
      itemId: item.itemId,
      selected: !!item.mySelection?.selected,
      expanded: !!item.mySelection?.selected,
      mode: item.mySelection?.intent ?? null,
      equalShareScope:
        item.mySelection?.intent === 'EQUAL_SHARE'
          ? item.mySelection.quantity === item.quantity
            ? 'FULL_ITEM'
            : 'PARTIAL_QUANTITY'
          : null,
      equalShareQuantity:
        item.mySelection?.intent === 'EQUAL_SHARE' && item.mySelection.quantity !== item.quantity
          ? item.mySelection.quantity
          : null,
      quantity: item.mySelection?.intent === 'BY_QUANTITY' ? item.mySelection.quantity : null,
      suggestedGroups: (item.mySelection?.consumptionGroups ?? []).map((group) => ({
        localId: crypto.randomUUID(),
        size: group.size,
        participation: group.participation === 'INDIVIDUAL' ? 'INDIVIDUAL' : 'SHARED',
      })),
    }));
  }

  private buildSaveDraftPayload(): SaveParticipantProposalsRequestDto {
    return {
      items: this.drafts().map((draft) => {
        const item = this.items().find((i) => i.itemId === draft.itemId);

        if (!draft.selected || !draft.mode) {
          return {
            itemId: draft.itemId,
            selected: false,
            consumptionGroups: [],
          };
        }

        if (draft.mode === 'BY_QUANTITY') {
          return {
            itemId: draft.itemId,
            selected: true,
            intent: 'BY_QUANTITY',
            quantity: draft.quantity ?? 1,
            consumptionGroups: [],
          };
        }

        if (draft.mode === 'EQUAL_SHARE') {
          return {
            itemId: draft.itemId,
            selected: true,
            intent: 'EQUAL_SHARE',
            quantity:
              draft.equalShareScope === 'FULL_ITEM'
                ? (item?.quantity ?? 1)
                : (draft.equalShareQuantity ?? 1),
            consumptionGroups: [],
          };
        }

        return {
          itemId: draft.itemId,
          selected: true,
          intent: 'UNRESOLVED',
          consumptionGroups: (draft.suggestedGroups ?? []).map((group) => ({
            size: group.size,
            participation: group.participation,
          })),
        };
      }),
    };
  }

  private isCurrentParticipantSubmitted(context: ParticipantBillSplitContextResponseDto): boolean {
    return context.participants.some(
      (participant) => participant.id === this.participantId && participant.status === 'SUBMITTED',
    );
  }

  private getSubmittedStorageKey(): string {
    return `dinno_participant_submitted_${this.splitId}`;
  }

  private wasSubmittedLocally(): boolean {
    return sessionStorage.getItem(this.getSubmittedStorageKey()) === 'true';
  }

  private handleError(err: HttpErrorLike, fallbackTitle: string, fallbackText: string): void {
    if (this.isParticipantRemovedError(err)) {
      this.handleParticipantRemoved();
      return;
    }

    this.sweetAlert.showError(fallbackTitle, fallbackText);
  }

  private isParticipantRemovedError(err: HttpErrorLike): boolean {
    const status = err?.status;
    const code = httpErrorCode(err);

    return (
      status === 404 ||
      status === 403 ||
      code === 'PARTICIPANT_REMOVED' ||
      code === 'PARTICIPANT_ACCESS_INVALID'
    );
  }

  private handleParticipantRemoved(): void {
    const splitId = this.splitId;

    sessionStorage.removeItem('dinno_join_split_id');
    sessionStorage.removeItem('dinno_join_participant_id');
    sessionStorage.removeItem('dinno_join_participant_name');
    sessionStorage.removeItem('dinno_join_participant_token');
    sessionStorage.removeItem('dinno_join_saved_at');
    sessionStorage.removeItem(`dinno_participant_submitted_${splitId}`);

    this.sweetAlert.showError(
      'Ya no formás parte de esta división',
      'El anfitrión te quitó de la cuenta. Podés volver a unirte desde el enlace.',
    );

    this.router.navigate(['/division-cuenta/join', splitId]);
  }

  private subscribeToRealtimeEvents(): void {
    if (!this.splitId || !this.participantToken) return;

    this.realtimeSub?.unsubscribe();

    this.realtimeSub = this.facade
      .listenParticipantSplitEvents(this.splitId, this.participantToken)
      .subscribe({
        next: (event: CollaborativeSseEvent) => {
          if (event.type === 'division-finalized') {
            this.handleDivisionFinalized();
          }
        },
        error: (err) => {
          logger.error(
            'División de cuenta',
            'Error en la conexión en tiempo real del participante',
            err,
          );
        },
      });
  }

  private handleDivisionFinalized(): void {
    this.router.navigate(['/division-cuenta', this.splitId], {
      queryParams: {
        participantToken: this.participantToken,
      },
    });
  }
}
